import cn from 'classnames';
import PropTypes from 'prop-types';
import { useId } from 'react';

// JSDoc type definitions
/**
 * @typedef LdsLoadingSpinnerProps
 * @property {number} [size] - Sets the height and width of the spinner in pixels; larger numbers render a larger spinner
 * @property {number} [animationSpeed] - Sets the apparent rotating animation speed of the spinner in milliseconds; lower numbers result in a faster spinner
 * @property {boolean} [decorative] - Whether the spinner should be decorative; when set it adds the ARIA hidden attribute to true which will cause the spinner to be ignored by assistive technology
 * @property {string} [ariaLabel] - The value of the ARIA label on the component which may be announced by assistive technologies
 * @property {string} [svgTitle] - The text value of the title tag within the SVG element that defines the spinner image; may sometimes be visible as a browser tooltip if the user hovers the spinner image
 * @property {string} [className] - Optional custom user-defined CSS class names, will be appended to the list of any other component classes
 */
/**
 * LdsLoadingSpinner component
 * @param {LdsLoadingSpinnerProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element}
 */
function LdsLoadingSpinner({
  size = 40,
  animationSpeed = 1000,
  decorative = false,
  ariaLabel = 'loading',
  svgTitle = 'loading',
  className = '',
  ...rest
}) {
  const dimSize = `${size}px`;
  const durAnimationSpeed = `${animationSpeed}ms`;
  const txtAriaLabel = ariaLabel || 'loading';
  const txtSvgTitle = svgTitle || txtAriaLabel;
  const titleId = useId();

  return (
    <div
      className={cn(
        'lds-loading-spinner',
        className
      )}
      style={{ width: dimSize, height: dimSize }}
      aria-label={decorative ? null : txtAriaLabel}
      aria-hidden={decorative ? 'true' : null}
      role={decorative ? null : 'alert'}
      aria-live={decorative ? null : 'assertive'}
      {...rest}
    >
      <svg
        className="lds-loading-spinner-svg"
        style={{
          width: dimSize,
          height: dimSize,
          animationDuration: durAnimationSpeed,
        }}
        viewBox="0 0 40 40"
        xmlns="http://www.w3.org/2000/svg"
        aria-labelledby={titleId} // Not all screen readers announce the title tag alone.
      >
        <title id={titleId}>
          {txtSvgTitle}
        </title>
        <defs>
          <linearGradient
            id="loading-spinner-gradient-id"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop
              className="grad-stop-1"
              offset="10%"
            />
            <stop
              className="grad-stop-2"
              offset="90%"
            />
          </linearGradient>
        </defs>
        <path
          className="lds-loading-spinner-svg-circle"
          d="M20,40V36A16,16,0,1,1,36,20,16,16,0,0,1,20,36v4A20,20,0,1,0,0,20,20,20,0,0,0,20,40Z"
        />
        <circle
          className="lds-loading-spinner-svg-cap"
          cx="20.1"
          cy="38"
          r="2"
        />
        <path
          style={{ fill: 'url(#loading-spinner-gradient-id)' }}
          className="lds-loading-spinner-svg-tail"
          d="M20,0V4a16,16,0,0,1,0,32v4A20,20,0,0,0,20,0Z"
        />
      </svg>
    </div>
  );
}

export default LdsLoadingSpinner;

LdsLoadingSpinner.propTypes = {
  ariaLabel: PropTypes.string,
  animationSpeed: PropTypes.number,
  className: PropTypes.string,
  decorative: PropTypes.bool,
  size: PropTypes.number,
  svgTitle: PropTypes.string,
};
